import React, { useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { AuthContext } from "../../../Context/AuthContextProvider";
import { useNavigate, Link } from "react-router-dom";
import { post } from "../../../utils/api";
import {
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Shield,
  Key,
  AlertCircle,
  User,
  Info,
} from "lucide-react";

function AddPassword() {
  const { loginUser, updateLoginUser } = useContext(AuthContext);
  const [Password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const navigate = useNavigate();

  // Validation criteria
  const passwordCriteria = {
    length: Password.length >= 8,
    uppercase: /[A-Z]/.test(Password),
    lowercase: /[a-z]/.test(Password),
    digit: /\d/.test(Password),
    specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(Password),
    match: Password === confirmPassword && confirmPassword.length > 0,
  };

  // Check if all conditions are met
  const isPasswordValid =
    passwordCriteria.length &&
    passwordCriteria.uppercase &&
    passwordCriteria.lowercase &&
    passwordCriteria.digit &&
    passwordCriteria.specialChar &&
    passwordCriteria.match;

  useEffect(() => {
    if (Password || confirmPassword) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
    }
  }, [Password, confirmPassword]);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error("Password does not meet the requirements");
      return;
    }

    if (Password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    const userId = loginUser._id;
    try {
      const response = await post("/sam/doc/users/add-password", {
        userId,
        Password,
      });

      setPassword("");
      setConfirmPassword("");
      setShowValidation(false);

      if (response.user) {
        await updateLoginUser(response.user);
        toast.success(response.message || "Password added successfully");
        navigate("/Dashboard/Profile");
      }
    } catch (error) {
      toast.error("Error adding password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-xl shadow-lg">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Add Password</h1>
            <p className="text-sm text-gray-600 mt-1">
              Create a password for your account
            </p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
            <Shield className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900">
              Google Account Security
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Even though you created your account with Google, adding a
              password gives you an additional way to login. You can continue
              using Google login or use your username with your new password.
            </p>
          </div>
        </div>

        {/* Add Password Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-[#fcdb6a]/20 to-[#b99653]/10 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-lg">
                <Key className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Password Creation
              </h2>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleChangePassword} className="p-6 space-y-6">
            {/* New Password */}
            <div className="space-y-2">
              <label
                htmlFor="newPassword"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <Lock className="w-4 h-4 text-[#b99653]" />
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  value={Password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setShowValidation(true);
                  }}
                  required
                  placeholder="Enter your new password"
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-[#b99653] focus:ring-2 focus:ring-[#b99653]/20 transition-all outline-none text-gray-900 font-medium"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-500" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label
                htmlFor="confirmPassword"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <Lock className="w-4 h-4 text-[#b99653]" />
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setShowValidation(true);
                  }}
                  required
                  placeholder="Re-enter your password"
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-[#b99653] focus:ring-2 focus:ring-[#b99653]/20 transition-all outline-none text-gray-900 font-medium"
                />
              </div>
            </div>

            {/* Password Requirements */}
            {showValidation && (
              <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-[#b99653]" />
                  Password Requirements
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    {passwordCriteria.length ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.length
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      At least 8 characters
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.uppercase ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.uppercase
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      One uppercase letter
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.lowercase ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.lowercase
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      One lowercase letter
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.digit ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.digit
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      One number
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.specialChar ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.specialChar
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      One special character
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.match ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.match
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      Passwords match
                    </span>
                  </div>
                </div>

                {/* Overall Status */}
                {isPasswordValid && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm font-semibold">
                        All requirements met! You can create your password.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show Password Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showPassword"
                checked={showPassword}
                onChange={() => setShowPassword(!showPassword)}
                className="w-4 h-4 rounded border-gray-300 text-[#b99653] focus:ring-[#b99653] cursor-pointer"
              />
              <label
                htmlFor="showPassword"
                className="text-sm text-gray-700 cursor-pointer select-none"
              >
                Show passwords
              </label>
            </div>

            {/* Form Actions */}
            <div className="flex flex-wrap gap-3 pt-4">
              <Link
                to="/Dashboard/Profile"
                className="px-6 py-3 rounded-xl font-semibold border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </Link>

              <button
                type="submit"
                disabled={loading || !isPasswordValid}
                className={`flex-grow flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  loading || !isPasswordValid
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white hover:shadow-lg hover:scale-[1.02]"
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Creating Password...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>Create Password</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Additional Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
          <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0">
            <Info className="w-5 h-5 text-yellow-700" />
          </div>
          <div>
            <p className="text-sm font-medium text-yellow-900">
              Account Information
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              After creating a password, you can login either through Google
              Login, or by using your username:{" "}
              <span className="font-semibold">{loginUser.username}</span> with
              your new password. If you encounter any issues, please{" "}
              <Link
                to="/marketplace-help"
                className="text-[#b99653] hover:underline font-medium"
              >
                contact our support team
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddPassword;
